/**
 * Clip retargeting through the ontology hub (ADR-003 §2, ADR-005 §3) — the
 * ABSOLUTE-TRANSPORT law (ADR-019, pose-faithful):
 *
 *   out_b(t) = F_p⁻¹ · srcRot_b(t) · F_b
 *
 * F_b is the bone's frame map; F_p is the frame map of the bone's nearest
 * MAPPED ancestor in the TARGET hierarchy (the output composes in the target
 * hierarchy, so the map that must cancel is the one the target parent's own
 * track used). At the TOP of the mapped chain F_p⁻¹ becomes the root bridge
 * A_t⁻¹·G·A_s — static unmapped-ancestor world binds each side, joined by
 * the declared convention rotation (see rootBridge). Down a mapped chain the
 * interior maps telescope away — world_tgt = F_root⁻¹ · world_src · F_leaf —
 * so a retargeted clip reproduces the SOURCE's world pose semantics instead
 * of layering source deltas over the target's own bind pose (the Gen-2
 * bind-relative delta law this replaced: its un-normalized rest offset is
 * what kept every A-pose Tripo generation's arms 40°+ low under T-pose
 * Mixamo clips — ADR-019 §1).
 *
 * AMENDMENT (2026-08-09, the clavicle exception): bones the ontology marks
 * restAnchoredTransport play out = P·srcRot·F_b·R with the constant anchor
 * R = (P·srcBind·F_b)⁻¹·tgtBind — source-rest lands on TARGET-rest because a
 * clavicle's rest direction is per-rig anatomy, not pose; children cancel R
 * through their prefix and stay pose-faithful (chest-collapse forensics:
 * plain absolute transport depressed every Tripo shoulder 10–17° and the
 * generator's bled clavicle weights collapsed the chest).
 *
 * F comes from the REORIENTATION STAGE (./reorient, the accepted C·B law):
 * child-direction evidence corrects the bind prior where the rigs' local
 * frames disagree semantically; unavailable evidence degrades to the bind
 * prior with the degradation named in the report; provable ambiguity throws —
 * for the bone's own map AND for its parent's (one door, ADR-019 §7).
 *
 * The position layer applies to the named root-motion channel only:
 * unit-ratio scaling and PROPORTIONAL HIP-DELTA scaling (bind offsets align
 * rest poses; motion excursions scale by the hip-HEIGHT ratio along each
 * rig's up axis — the 2026-07-11 lesson that kept a Mixamo death from
 * driving a small rig through the floor).
 *
 * TRACK-SPACE CONTRACT (ADR-008 §2b): ClipData translation values live in
 * the bone's localBind space — parent-BONE-relative, interposed transforms
 * folded (the import side of this contract landed in PR #17). The position
 * layer therefore lifts source values through the source hips-parent world
 * bind before measuring excursions, and expresses its output through the
 * inverse of the TARGET hips-parent world bind. Emitting world values as if
 * they were local drove a Tripo Z-up-rooted rig's forward travel onto world
 * UP — first light's flying-leg beam (V2_RETARGET_ENGINE_DEEP_DIVE §7).
 *
 * Depends on: math/quat, math/vec3, ir/*, ./frame-map, ./reorient.
 */
import {
  quatContinuous,
  quatInvertUnit,
  quatMultiply,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { type Vec3Data, vec3Add, vec3Length, vec3Normalize, vec3Scale, vec3Sub } from '../math/vec3.js';
import { KernelError, ReflectedBindFrameError, UnmappedBoneError } from '../ir/errors.js';
import { jointFrameFor, sourceRestAnchored, type JointFrame, type PriorsBundle } from '../ir/priors.js';
import { swingTwist } from '../math/swing-twist.js';
import type { Ontology, OntologyBone } from '../ir/ontology.js';
import { requireProfileCoverage, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import { parseClipData, type ClipData, type ClipTrack } from '../ir/clip-data.js';
import { conventionRotation, hubToWorld } from './convention.js';
import {
  corroboratePostureWitnesses,
  corroborateTwistWitnesses,
  type PostureWitnessContradiction,
  type WitnessContradiction,
} from './witness-corroboration.js';
import { measuredAnteriorFrame, vetAuthoredFrame, type AuthoredFrameRefusal } from './authored-frame-guard.js';
import {
  computeReorientation,
  ReorientationUndeterminedError,
  type ReorientBoneSide,
  type ReorientFrameMap,
  type ReorientResult,
} from './reorient.js';

export interface RetargetOptions {
  /** Ontology profile both rigs must cover (e.g. 'minimal'). */
  readonly profile: string;
  /** Authored joint-frame overrides (PR #24) + each rig's declared family —
   *  when provided, an authored (family, bone) entry becomes that side's frame
   *  (evidence 'authored-frame', ADR-010 §11: the hand wins). */
  readonly priors?: PriorsBundle;
  readonly sourceFamily?: string;
  readonly targetFamily?: string;
}

export interface ReorientReportEntry {
  /** Target rig bone the frame map applies to. */
  readonly target: string;
  readonly confidence: ReorientFrameMap['confidence'];
  readonly evidence: ReorientFrameMap['evidence'];
  readonly degradedBecause: ReorientFrameMap['degradedBecause'];
  readonly residualRadians: number | null;
  /** True when this bone played under rest-anchored transport (ADR-019
   *  amendment, the clavicle class) — its rest maps to the TARGET's rest. */
  readonly restAnchored: boolean;
  /** True when this bone played under twist-anchored transport (ADR-019 §2.2,
   *  the thumb chain): the target's own bind roll kept, swing transported. */
  readonly twistAnchored: boolean;
  /** True when this bone's children ride its anchor rather than cancel it
   *  (ADR-019 §2.2, the thumb chain). */
  readonly childrenRide: boolean;
}

/** A bone whose frame was measured to match the other side's anterior frame. */
export interface AnteriorFrameCompletion {
  readonly ontologyId: string;
  readonly bone: string;
  readonly side: 'source' | 'target';
  /** Family of the other side's anterior frame. */
  readonly pairedWith: string;
}

/** A twist-anchored bone whose axis (its primary child) is unmapped on the target. */
export class TwistAnchorAxisError extends KernelError {
  constructor(readonly bone: string) {
    super(
      `twist-anchored transport on "${bone}" has no axis: its primary child is unmapped on the target, ` +
        `so the roll to keep cannot be told from the swing to transport (ADR-019 §2.2)`,
    );
    this.name = 'TwistAnchorAxisError';
  }
}

export interface RetargetReport {
  readonly retargetedRotationTracks: number;
  /** Source tracks with no ontology mapping — dropped, but never silently. */
  readonly droppedTracks: readonly string[];
  /** Per retargeted bone: how its frame map was evidenced (ADR-003 §11). */
  readonly reorientation: readonly ReorientReportEntry[];
  /**
   * Mirror pairs whose twist witnesses disagreed across their own rig's
   * sagittal plane beyond derived uncertainty (authoring noise, e.g.
   * near-straight generated legs) — both witnesses demoted to the canonical
   * tier, loudly. Empty = every checkable pair corroborated.
   */
  readonly twistWitnessContradictions: readonly WitnessContradiction[];
  /**
   * Family-authored joint frames the rig's own geometry CONTRADICTED — each
   * refused and replaced (or handed to the ladder), named here so a name-keyed
   * family meeting another generator's roll is never silent
   * (retarget/authored-frame-guard; Tripo rig presets, 2026-09-23). Empty =
   * every offered authored frame agreed with its rig.
   */
  readonly authoredFrameRefusals: readonly AuthoredFrameRefusal[];
  /**
   * Center bones whose grandchild witnesses sat on opposite sides of the body
   * across the two rigs (spinal curvature = posture, not a frame) — both
   * demoted to the canonical tier, named here (Tripo presets, 2026-09-23).
   */
  readonly postureWitnessContradictions: readonly PostureWitnessContradiction[];
  /**
   * Bones given their MEASURED anterior frame because the other side's frame
   * names anterior and this side had none — the pair speaks one semantic
   * (retarget/authored-frame-guard measuredAnteriorFrame).
   */
  readonly anteriorFrameCompletions: readonly AnteriorFrameCompletion[];
  readonly rootMotion: {
    readonly applied: boolean;
    readonly unitScale: number;
    readonly deltaRatio: number;
    /** Declared-convention rotation applied to excursions; false = rigs agree. */
    readonly conventionApplied: boolean;
    /** Parent bones whose world binds framed the conversion — null = rig root (identity). */
    readonly sourceParentBone: string | null;
    readonly targetParentBone: string | null;
  } | null;
}

export interface RetargetResult {
  readonly clip: ClipData;
  readonly report: RetargetReport;
}

interface BonePair {
  readonly source: RigBone;
  readonly target: RigBone;
  /** The C·B result, computed once per pair; consulted per rotation track. */
  readonly reorient: ReorientResult;
  /**
   * Rest-anchored transport (ADR-019 amendment, ontology
   * restAnchoredTransport — the clavicle class): the bone's output gains a
   * constant anchor R = (P·srcBind·F)⁻¹·tgtBind so source-rest plays as
   * TARGET-rest, while source motion still transports as world-faithful
   * left-deltas. A clavicle's rest direction is per-rig ANATOMY (Tripo's
   * slopes ~10° up to the acromion, Mixamo's is horizontal — same neutral
   * shoulders); plain absolute transport matched the source's world
   * direction and depressed every Tripo shoulder 10–17°, which the
   * generator's bled clavicle weights turned into the measured chest
   * collapse (forensics 2026-08-09). Children stay pose-faithful: their
   * prefix gains the constant R_p⁻¹, which cancels the anchor exactly.
   */
  readonly restAnchored: boolean;
  /**
   * Twist-anchored transport (ADR-019 §2.2, ontology twistAnchoredTransport —
   * the thumb chain): only the TWIST of the rest anchor is kept, about the
   * target bone's own axis, so source-rest lands with the target's own bind
   * roll (the receiver keeps its nail) while the swing still transports
   * world-faithfully. Unlike the rest anchor, children RIDE it — a digit is
   * one nail, and a phalanx that cancelled its metacarpal's anchor would
   * re-introduce the twist at the knuckle. Measured 2026-08-25: Mixamo's
   * thumb rest roll transported onto a Tripo graft rolled the metacarpal
   * 48–82° from the character's bind before any motion.
   */
  readonly twistAnchored: boolean;
  /**
   * Children ride this bone's anchor (ontology anchorChildrenRide) instead of
   * cancelling it in their prefix: a digit is one thing, so the thumb's
   * phalanges wear the metacarpal's kept rest; the clavicle's arm cancels
   * (ADR-019 §2.1 vs §2.2).
   */
  readonly childrenRide: boolean;
  /**
   * The target bone's axis in its own local bind frame (toward its primary
   * child), the twist anchor's axis. Null when the child is unmapped — a
   * twist-anchored pair without it throws at play (never a silent no-op).
   */
  readonly targetAxisLocal: Vec3Data | null;
}

/** Co-location floor for child offsets: 0.1 mm in physical terms. */
const REORIENT_MIN_BONE_METERS = 1e-4;

/** ontology-ID → the rig bone mapped to it (for child/grandchild resolution). */
function bonesByOntologyId(rig: RigDescriptor): Map<string, RigBone> {
  const byName = new Map(rig.bones.map((b) => [b.name, b]));
  const out = new Map<string, RigBone>();
  for (const m of rig.mapping) {
    const bone = byName.get(m.rigBone);
    if (bone) out.set(m.ontologyId, bone);
  }
  return out;
}

/**
 * The ontology children of `ontologyId` that form a `mirror` pair, returned
 * `[leftId, rightId]` (left first, so the lateral-spread sign is consistent
 * across rigs). Feeds the bilateral-child reorientation tier for co-located
 * center bones — e.g. Hips → [LeftUpLeg, RightUpLeg] (design note:
 * REORIENT_BILATERAL_CHILD_FRAME). null when the bone has no such pair.
 */
function bilateralChildIds(ontology: Ontology, ontologyId: string): readonly [string, string] | null {
  for (const c of ontology.bones) {
    if (c.parent !== ontologyId || c.side !== 'left' || c.mirror === null) continue;
    const twin = ontology.byId.get(c.mirror);
    if (twin && twin.parent === ontologyId && twin.side === 'right') return [c.id, twin.id];
  }
  return null;
}

function reorientSide(
  bone: RigBone,
  byOntologyId: Map<string, RigBone>,
  primaryChildId: string | null,
  grandchildId: string | null,
  bilateralIds: readonly [string, string] | null,
  ontologyBone: OntologyBone | undefined,
  hub: ((v: Vec3Data) => Vec3Data) | null,
  witnessDisqualified: boolean,
  authored: JointFrame | null,
): ReorientBoneSide {
  const positionOf = (id: string | null): Vec3Data | null => {
    if (id === null) return null;
    return byOntologyId.get(id)?.worldBind.position ?? null;
  };
  // Bilateral mirror-pair child positions (both must be mapped) — the
  // co-located-center-bone frame source; null unless the whole pair resolves.
  let bilateralChildPositions: readonly [Vec3Data, Vec3Data] | null = null;
  if (bilateralIds !== null) {
    const left = positionOf(bilateralIds[0]);
    const right = positionOf(bilateralIds[1]);
    if (left !== null && right !== null) bilateralChildPositions = [left, right];
  }
  // The §11 canonical secondary, carried into this bone's LOCAL frame:
  // hub axes → rig world axes (declared up/facing) → bone local (world bind).
  // Declared-reference evidence — it assumes the bind is authored in the
  // declared convention, so reorient ranks it below measured grandchildren.
  let canonicalSecondaryLocal: Vec3Data | null = null;
  let declaredPrimaryLocal: Vec3Data | null = null;
  const hubSecondary = ontologyBone?.zeroPoseSecondary ?? null;
  const hubPrimary = ontologyBone?.zeroPoseDirection ?? null;
  if (hub !== null) {
    const worldRotInv = quatInvertUnit(quatNormalize(bone.worldBind.rotation));
    if (hubSecondary !== null) canonicalSecondaryLocal = quatRotateVec3(worldRotInv, hub(hubSecondary));
    if (hubPrimary !== null) declaredPrimaryLocal = quatRotateVec3(worldRotInv, hub(hubPrimary));
  }
  return {
    localBind: bone.localBind.rotation,
    worldBind: { position: bone.worldBind.position, rotation: bone.worldBind.rotation },
    childWorldBindPosition: positionOf(primaryChildId),
    // A witness disqualified by cross-side contradiction must not pin twist:
    // suppressing the grandchild here drops the ladder to the canonical tier
    // (same-kind anatomy, side-consistent by construction) — never silently.
    grandchildWorldBindPosition: witnessDisqualified ? null : positionOf(grandchildId),
    canonicalSecondaryLocal,
    declaredPrimaryLocal,
    bilateralChildPositions,
    // Authored joint-frame override for this (family, bone), when the caller
    // supplied priors + this rig's family (PR #24) — the reorientation ladder's
    // authored tier; null = no entry, ladder uses its measured/declared tiers.
    authoredPrimaryLocal: authored?.localPrimary ?? null,
    authoredSecondaryLocal: authored?.localSecondary ?? null,
  };
}

/** rig-bone-name → its counterpart on the other rig, through the ontology hub. */
function buildPairs(
  ontology: Ontology,
  source: RigDescriptor,
  target: RigDescriptor,
  options: RetargetOptions,
): {
  pairs: Map<string, BonePair>;
  witnessContradictions: WitnessContradiction[];
  authoredFrameRefusals: AuthoredFrameRefusal[];
  postureContradictions: readonly PostureWitnessContradiction[];
  anteriorCompletions: AnteriorFrameCompletion[];
} {
  const srcByName = new Map(source.bones.map((b) => [b.name, b]));
  const srcByOntologyId = bonesByOntologyId(source);
  const tgtByOntologyId = bonesByOntologyId(target);
  const srcTolerance = REORIENT_MIN_BONE_METERS / source.units.metersPerUnit;
  const tgtTolerance = REORIENT_MIN_BONE_METERS / target.units.metersPerUnit;
  const srcHub = hubToWorld(source);
  const tgtHub = hubToWorld(target);

  // Cross-side witness corroboration (per rig, pre-pass): a mirror pair
  // whose twist witnesses disagree beyond derived uncertainty loses BOTH
  // witnesses on that rig — flag, don't average, don't pick a side.
  const srcWitness = corroborateTwistWitnesses(source, srcByOntologyId, ontology, srcTolerance);
  const tgtWitness = corroborateTwistWitnesses(target, tgtByOntologyId, ontology, tgtTolerance);
  const witnessContradictions = [...srcWitness.contradictions, ...tgtWitness.contradictions];
  // Cross-rig pre-pass for the center chain: a spine witness is posture, and
  // opposite curvature at one level must not pin the twist on either side.
  const posture = corroboratePostureWitnesses(
    source, srcByOntologyId, srcTolerance, target, tgtByOntologyId, tgtTolerance, ontology,
  );

  // Each rig's anatomical forward at bind (hub +Z through its declared,
  // possibly measured-promoted, convention) — the secondary check's judge.
  const srcAnterior = srcHub === null ? null : srcHub({ x: 0, y: 0, z: 1 });
  const tgtAnterior = tgtHub === null ? null : tgtHub({ x: 0, y: 0, z: 1 });
  const authoredFrameRefusals: AuthoredFrameRefusal[] = [];
  const anteriorCompletions: AnteriorFrameCompletion[] = [];
  // Promote an authored frame only when the rig's geometry agrees with it.
  const vetted = (
    frame: JointFrame | null,
    bone: RigBone,
    byOntologyId: Map<string, RigBone>,
    childId: string | null,
    anterior: Vec3Data | null,
    tolerance: number,
    side: 'source' | 'target',
  ): JointFrame | null => {
    if (frame === null) return null;
    const verdict = vetAuthoredFrame({
      frame,
      bone,
      childWorldPosition: childId === null ? null : (byOntologyId.get(childId)?.worldBind.position ?? null),
      anteriorWorld: anterior,
      tolerance,
      side,
    });
    if (verdict.refusal !== null) authoredFrameRefusals.push(verdict.refusal);
    return verdict.frame;
  };

  const pairs = new Map<string, BonePair>();
  for (const m of source.mapping) {
    const src = srcByName.get(m.rigBone);
    const tgt = tgtByOntologyId.get(m.ontologyId);
    if (!src || !tgt) continue;
    // Child geometry resolves through the hub's declared primaryChild chain
    // (§11) — never by guessing among a rig bone's children. An ontology ID
    // the loaded ontology doesn't know yields no child geometry and degrades
    // to the bind prior, reported.
    const ontologyBone = ontology.byId.get(m.ontologyId);
    const primaryChildId = ontologyBone?.primaryChild ?? null;
    const grandchildId =
      primaryChildId === null ? null : (ontology.byId.get(primaryChildId)?.primaryChild ?? null);
    // Bilateral mirror-pair children (e.g. Hips → LeftUpLeg/RightUpLeg) — the
    // co-located-center-bone frame source; identical on both rigs (hub-driven).
    const bilateralIds = bilateralChildIds(ontology, m.ontologyId);
    // Authored joint-frame overrides (PR #24), resolved per side by the rig's
    // declared family — an authored (family, bone) entry becomes that side's
    // frame at evidence 'authored-frame' (the hand wins, ADR-010 §11). Absent
    // priors or family ⇒ null on that side ⇒ the ladder runs unchanged.
    // Every authored frame passes the agreement guard first, on BOTH sides:
    // a family read from names can meet a skeleton with another generator's
    // roll (Tripo presets), and a frame the rig contradicts is refused.
    let srcAuthored = vetted(
      options.priors !== undefined && options.sourceFamily !== undefined
        ? jointFrameFor(options.priors, options.sourceFamily, m.ontologyId, options.targetFamily, 'source')
        : null,
      src, srcByOntologyId, primaryChildId, srcAnterior, srcTolerance, 'source',
    );
    let tgtAuthored = vetted(
      options.priors !== undefined && options.targetFamily !== undefined
        ? jointFrameFor(options.priors, options.targetFamily, m.ontologyId, options.sourceFamily, 'target')
        : null,
      tgt, tgtByOntologyId, primaryChildId, tgtAnterior, tgtTolerance, 'target',
    );
    // Same semantic on both sides: when one side's frame names ANTERIOR and
    // the other has none, the other side's measured anterior frame completes
    // the pair — its canonical fallback would name the ontology's secondary
    // (UP on the arm chain), a built-in quarter-turn.
    const childPosition = (byId: Map<string, RigBone>): Vec3Data | null =>
      primaryChildId === null ? null : (byId.get(primaryChildId)?.worldBind.position ?? null);
    if (srcAuthored?.secondaryMeans === 'anterior' && tgtAuthored === null) {
      tgtAuthored = measuredAnteriorFrame({
        family: options.targetFamily ?? 'unknown', ontologyId: m.ontologyId, bone: tgt,
        childWorldPosition: childPosition(tgtByOntologyId), anteriorWorld: tgtAnterior, tolerance: tgtTolerance,
      });
      if (tgtAuthored !== null) anteriorCompletions.push({ ontologyId: m.ontologyId, bone: tgt.name, side: 'target', pairedWith: srcAuthored.family });
    } else if (tgtAuthored?.secondaryMeans === 'anterior' && srcAuthored === null) {
      srcAuthored = measuredAnteriorFrame({
        family: options.sourceFamily ?? 'unknown', ontologyId: m.ontologyId, bone: src,
        childWorldPosition: childPosition(srcByOntologyId), anteriorWorld: srcAnterior, tolerance: srcTolerance,
      });
      if (srcAuthored !== null) anteriorCompletions.push({ ontologyId: m.ontologyId, bone: src.name, side: 'source', pairedWith: tgtAuthored.family });
    }
    const reorient = computeReorientation({
      source: reorientSide(
        src, srcByOntologyId, primaryChildId, grandchildId, bilateralIds, ontologyBone, srcHub,
        srcWitness.disqualified.has(m.ontologyId) || posture.disqualified.has(m.ontologyId), srcAuthored,
      ),
      target: reorientSide(
        tgt, tgtByOntologyId, primaryChildId, grandchildId, bilateralIds, ontologyBone, tgtHub,
        tgtWitness.disqualified.has(m.ontologyId) || posture.disqualified.has(m.ontologyId), tgtAuthored,
      ),
      childDirectionInformative: ontologyBone?.childDirectionInformative ?? true,
      sourceTolerance: srcTolerance,
      targetTolerance: tgtTolerance,
    });
    const tgtChild = primaryChildId === null ? null : (tgtByOntologyId.get(primaryChildId) ?? null);
    const targetAxisLocal =
      tgtChild === null
        ? null
        : (() => {
            // The bone's axis in the space its LOCAL ROTATION ACTS ON — undo
            // the world rotation, and nothing else. A node places its child at
            // W_parent · T·R·(S·t_child), so R acts on the SCALED offset: a
            // twist about normalize(S·t_child) leaves the child exactly where
            // it is, while a twist about the unscaled t_child direction moves
            // it (91.8 mm for the fixture below). Dividing this offset by the
            // bind scale would name a direction the rotation never acts on —
            // measured, and pinned by the scaled-axis test (Codex P2, #112).
            const local = quatRotateVec3(
              quatInvertUnit(quatNormalize(tgt.worldBind.rotation)),
              vec3Sub(tgtChild.worldBind.position, tgt.worldBind.position),
            );
            return vec3Length(local) < tgtTolerance ? null : vec3Normalize(local);
          })();
    pairs.set(m.rigBone, {
      source: src,
      target: tgt,
      reorient,
      // The ontology's class-wide declaration, OR a source family whose clips
      // are rest-relative (SL's axial chain — the pelvis lean, 2026-09-22).
      restAnchored:
        (ontologyBone?.restAnchoredTransport ?? false) ||
        (options.priors !== undefined &&
          sourceRestAnchored(options.priors, options.sourceFamily, options.targetFamily, m.ontologyId)),
      twistAnchored: ontologyBone?.twistAnchoredTransport ?? false,
      childrenRide: ontologyBone?.anchorChildrenRide ?? false,
      targetAxisLocal,
    });
  }
  return {
    pairs,
    witnessContradictions,
    authoredFrameRefusals,
    postureContradictions: posture.contradictions,
    anteriorCompletions,
  };
}

/**
 * For each pair (keyed by source rig bone name), the pair of its nearest
 * MAPPED ancestor in the TARGET hierarchy — the F_p of the absolute-transport
 * law (ADR-019 §2). The output composes in the target hierarchy, so the map
 * that must telescope away is the one the target parent's own track used.
 * null = no mapped ancestor (root-level bones; F_p = identity).
 */
function parentPairsByTargetChain(
  pairs: Map<string, BonePair>,
  targetRig: RigDescriptor,
): Map<string, BonePair | null> {
  const byTargetName = new Map<string, BonePair>();
  for (const p of pairs.values()) byTargetName.set(p.target.name, p);
  const tgtByName = new Map(targetRig.bones.map((b) => [b.name, b]));
  const out = new Map<string, BonePair | null>();
  for (const [key, p] of pairs) {
    let parent: BonePair | null = null;
    for (
      let ancestor = p.target.parent;
      ancestor !== null;
      ancestor = tgtByName.get(ancestor)?.parent ?? null
    ) {
      const candidate = byTargetName.get(ancestor);
      if (candidate !== undefined) {
        parent = candidate;
        break;
      }
    }
    out.set(key, parent);
  }
  return out;
}

function guardReflections(rig: RigDescriptor, label: string): void {
  const first = rig.reflectedPairs[0];
  if (first !== undefined) {
    // Until the declared mirror-plane/parity metadata lands (accepted Sol
    // proposal → ADR-003 minor bump), reflection-related binds are rejected
    // loudly — never mangled (ADR-005 §7).
    throw new ReflectedBindFrameError([`${label}:${first[0]}`, `${label}:${first[1]}`]);
  }
}

/**
 * Thrown if absolute transport ever meets an improper (reflection) frame map:
 * the law multiplies raw frame-map quaternions, and a reflection has no
 * quaternion form — that branch is deferred with the declared mirror-plane
 * metadata (ADR-019 §7). Unreachable today: guardReflections rejects
 * reflected binds before any map is built. Kept loud, never silent.
 */
export class ImproperFrameMapTransportError extends KernelError {
  constructor(readonly boneName: string) {
    super(
      `Bone "${boneName}" carries an improper (reflection) frame map — absolute ` +
        `transport (ADR-019) is scoped to proper maps. Reflected binds are rejected ` +
        `upstream (guardReflections) until declared mirror-plane metadata lands.`,
    );
  }
}

/**
 * The pair's frame map as a unit quaternion — the ONE undetermined door
 * (ADR-019 §7): a provably ambiguous reorientation throws here whether the
 * pair is the tracked bone itself or the parent its output composes through,
 * always naming the bone that needs the repair. Normalized ONCE per track —
 * a scaled-but-equivalent quat fed raw would scale every output key (Codex
 * review, PR #4).
 */
function properRotationOf(pair: BonePair): Quat {
  if (pair.reorient.kind === 'undetermined') {
    // The bind prior is PROVABLY wrong here (exact anti-parallel children)
    // and the evidence cannot pick the flip axis — shipping it would mangle
    // motion by 180°. Fail loud with the named repair (ADR-005 §7 spirit).
    throw new ReorientationUndeterminedError(
      pair.target.name,
      pair.reorient.reason,
      pair.reorient.needs,
    );
  }
  const frameMap = pair.reorient.frameMap;
  if (frameMap.kind !== 'proper') throw new ImproperFrameMapTransportError(pair.target.name);
  return quatNormalize(frameMap.rotation);
}

const IDENTITY_QUAT: Quat = { x: 0, y: 0, z: 0, w: 1 };

/** The bone's parent's STATIC world-bind rotation (identity at the rig root) —
 *  worldBind already composes every ancestor, so one lookup carries the whole
 *  unmapped chain above a root-level pair. */
function parentWorldBindRotation(bone: RigBone, rig: RigDescriptor): Quat {
  if (bone.parent === null) return IDENTITY_QUAT;
  const parent = rig.bones.find((b) => b.name === bone.parent);
  return parent === undefined ? IDENTITY_QUAT : quatNormalize(parent.worldBind.rotation);
}

/**
 * Thrown when a bone's parent PAIR (found by the target-hierarchy walk) is
 * not an ancestor of the bone on the SOURCE side — the two rigs genuinely
 * disagree about which mapped bone owns this chain. A static prefix is
 * provably impossible there (the bones between move at play time); transport
 * would need per-key source-world sampling, which is deliberately out of
 * ADR-019's scope. Refuse loudly, never approximate silently.
 */
export class DivergedTopologyError extends KernelError {
  constructor(
    readonly boneName: string,
    readonly targetAncestor: string,
    readonly sourceCounterpart: string,
  ) {
    super(
      `Bone "${boneName}": its nearest mapped ancestor on the target maps to source ` +
        `bone "${sourceCounterpart}", which is NOT an ancestor of it on the source rig — ` +
        `the rigs disagree about which mapped bone owns this chain. Absolute transport ` +
        `(ADR-019) supports static interposed bones only; diverged topology needs ` +
        `per-key source-world sampling (not built).`,
    );
  }
}

/**
 * The static rotation interposed between `ancestor` and `bone` — the composed
 * world-bind rotation of the UNMAPPED bones between them:
 * I = wbr(ancestor)⁻¹ · wbr(parent(bone)). Identity in the common case where
 * the bone's direct parent IS the mapped ancestor. `requireAncestry` guards
 * the source side (target-side ancestry holds by construction of the
 * parent-pair walk): a non-ancestor means diverged topology → typed refusal.
 */
function interposedStatic(
  bone: RigBone,
  ancestor: RigBone,
  rig: RigDescriptor,
  requireAncestry: string | null,
): Quat {
  if (bone.parent === ancestor.name) return IDENTITY_QUAT;
  if (requireAncestry !== null) {
    const byName = new Map(rig.bones.map((b) => [b.name, b]));
    let found = false;
    for (let cur = bone.parent; cur !== null; cur = byName.get(cur)?.parent ?? null) {
      if (cur === ancestor.name) {
        found = true;
        break;
      }
    }
    if (!found) throw new DivergedTopologyError(requireAncestry, bone.name, ancestor.name);
  }
  return quatMultiply(
    quatInvertUnit(quatNormalize(ancestor.worldBind.rotation)),
    parentWorldBindRotation(bone, rig),
  );
}

/**
 * The absolute-transport prefix P of ADR-019 §2 — everything that stands to
 * the LEFT of srcRot in out = P · srcRot · F_b:
 *
 * - Through a mapped ancestor pair: P = I_t⁻¹ · F_p⁻¹ · I_s, where I_t/I_s
 *   are the STATIC binds of unmapped bones interposed on each side (identity
 *   for direct parent-child chains). The goblin's co-located, heavily rotated
 *   unmapped Pelvis between Hip and each Thigh is the measured case: without
 *   I_t the legs swing in the Pelvis's axis-soup frame (67%-of-hip-height
 *   lateral foot flail, world-motion regression 2026-07-31).
 * - At the TOP of the mapped chain: P = A_t⁻¹ · G · A_s — the same shape with
 *   the full static ancestor chains and the declared source→target convention
 *   rotation G standing in for the (nonexistent) parent map. This is the
 *   telescoping law's own boundary term: a plain identity here tipped the
 *   goblin's hips 90° through her Z-up Root and moved the walk's yaw onto the
 *   wrong world axis. Interior bones need neither: their parents' terms
 *   cancel by construction.
 * - Through a REST-ANCHORED ancestor pair (ADR-019 amendment): the parent's
 *   output is out_abs·R_p, so the child's prefix gains the constant R_p⁻¹ —
 *   P = I_t⁻¹ · R_p⁻¹ · F_p⁻¹ · I_s — which cancels the anchor exactly and
 *   keeps every descendant pose-faithful (the arm must not ride the shoulder
 *   correction). `parentAnchor` is non-null exactly when the parent pair is
 *   rest-anchored.
 */
function transportPrefix(
  pair: BonePair,
  parentPair: BonePair | null,
  sourceRig: RigDescriptor,
  targetRig: RigDescriptor,
  convention: Quat | null,
  parentAnchor: Quat | null,
): Quat {
  if (parentPair === null) {
    const aS = parentWorldBindRotation(pair.source, sourceRig);
    const aT = parentWorldBindRotation(pair.target, targetRig);
    const bridged = convention === null ? aS : quatMultiply(convention, aS);
    return quatMultiply(quatInvertUnit(aT), bridged);
  }
  const parentMapInv = quatInvertUnit(properRotationOf(parentPair));
  const withAnchor =
    parentAnchor === null
      ? parentMapInv
      : quatMultiply(quatInvertUnit(parentAnchor), parentMapInv);
  const iT = interposedStatic(pair.target, parentPair.target, targetRig, null);
  const iS = interposedStatic(pair.source, parentPair.source, sourceRig, pair.source.name);
  return quatMultiply(quatInvertUnit(iT), quatMultiply(withAnchor, iS));
}

function retargetRotationTrack(
  track: ClipTrack,
  pair: BonePair,
  prefix: Quat,
  anchor: Quat | null,
): ClipTrack {
  // The absolute-transport law (ADR-019 §2): out = P · srcRot · F_b, with
  // P from transportPrefix. Source and target BINDS no longer appear — pose
  // faithfulness IS the removal of the target's rest offset from every key.
  // Rest-anchored bones (the clavicle class) append the constant anchor R:
  // out = P · srcRot · F_b · R, so source-REST lands exactly on target-rest
  // while motion deltas stay world-faithful (left-deltas are unchanged by a
  // constant right factor).
  const rawMap = properRotationOf(pair);
  const boneMap = anchor === null ? rawMap : quatMultiply(rawMap, anchor);

  const values = new Float32Array(track.values.length);
  let prev: Quat | null = null;
  for (let k = 0; k < track.times.length; k += 1) {
    const i = k * 4;
    const srcRot: Quat = {
      x: track.values[i] as number,
      y: track.values[i + 1] as number,
      z: track.values[i + 2] as number,
      w: track.values[i + 3] as number,
    };
    const out2 = quatMultiply(prefix, quatMultiply(srcRot, boneMap));
    // The Gen-2 continuity flip: keep interpolation on the short arc.
    const out: Quat = prev !== null ? quatContinuous(out2, prev) : out2;
    prev = out;
    values[i] = out.x;
    values[i + 1] = out.y;
    values[i + 2] = out.z;
    values[i + 3] = out.w;
  }
  return { target: pair.target.name, path: 'rotation', times: track.times.slice(), values };
}

const DELTA_RATIO_MIN = 0.1;
const DELTA_RATIO_MAX = 10;

/** Bind height along the rig's declared up axis — the humanoid hip height. */
function upComponent(v: Vec3Data, upAxis: RigDescriptor['upAxis']): number {
  return Math.abs(upAxis === 'Y' ? v.y : v.z);
}

/** A parent bone's world bind — the frame that track-space values live under. */
interface ParentFrame {
  readonly name: string;
  readonly position: Vec3Data;
  readonly rotation: Quat;
  readonly scale: Vec3Data;
}

/** Thrown when a hips-parent world bind cannot frame positions (zero/non-finite scale). */
export class DegenerateParentFrameError extends KernelError {
  constructor(
    readonly boneName: string,
    readonly scale: Vec3Data,
  ) {
    super(
      `Root-motion parent bone "${boneName}" has a degenerate world-bind scale ` +
        `(${scale.x}, ${scale.y}, ${scale.z}) — positions cannot be framed through it.`,
    );
  }
}

/**
 * Thrown when the source root-motion carrier sits under an ANIMATED ancestor.
 * The position layer lifts samples through the ancestors' STATIC world binds;
 * an ancestor with its own tracks (a moving/rotating Root — common in BVH
 * exports) would make that lift silently wrong: its rotation redirects every
 * hips excursion and its translation vanishes from the output. Refuse loudly
 * until per-sample parent-trajectory evaluation is implemented (Sol review,
 * PR #18).
 */
export class AnimatedParentTrajectoryError extends KernelError {
  constructor(
    readonly rootMotionBone: string,
    readonly animatedAncestor: string,
    readonly ancestorTracks: readonly string[],
  ) {
    super(
      `Root-motion bone "${rootMotionBone}" has an animated ancestor "${animatedAncestor}" ` +
        `(${ancestorTracks.join(', ')}) — root motion is lifted through STATIC ancestor binds, ` +
        `so an animated ancestor would be silently ignored. Needs per-sample parent-trajectory ` +
        `evaluation (not yet implemented), or a source whose root motion is carried entirely ` +
        `on the hips bone.`,
    );
  }
}

const MIN_PARENT_SCALE = 1e-9;

function requireFramableScale(frame: ParentFrame): void {
  const { scale } = frame;
  for (const c of [scale.x, scale.y, scale.z]) {
    if (!Number.isFinite(c) || Math.abs(c) < MIN_PARENT_SCALE) {
      throw new DegenerateParentFrameError(frame.name, scale);
    }
  }
}

/** localBind-space position → world, through the parent's world bind (null = rig root). */
function parentToWorld(p: Vec3Data, frame: ParentFrame | null): Vec3Data {
  if (frame === null) return p;
  const scaled = { x: p.x * frame.scale.x, y: p.y * frame.scale.y, z: p.z * frame.scale.z };
  return vec3Add(frame.position, quatRotateVec3(quatNormalize(frame.rotation), scaled));
}

/** world position → the parent's localBind space (null = rig root). */
function worldToParent(w: Vec3Data, frame: ParentFrame | null): Vec3Data {
  if (frame === null) return w;
  const rotated = quatRotateVec3(quatInvertUnit(quatNormalize(frame.rotation)), vec3Sub(w, frame.position));
  return { x: rotated.x / frame.scale.x, y: rotated.y / frame.scale.y, z: rotated.z / frame.scale.z };
}

function retargetRootPosition(
  track: ClipTrack,
  pair: BonePair,
  srcParent: ParentFrame | null,
  tgtParent: ParentFrame | null,
  unitScale: number,
  srcUpAxis: RigDescriptor['upAxis'],
  tgtUpAxis: RigDescriptor['upAxis'],
  convention: Quat | null,
): { track: ClipTrack; deltaRatio: number } {
  if (srcParent !== null) requireFramableScale(srcParent);
  if (tgtParent !== null) requireFramableScale(tgtParent);
  const scaledSrcBind = vec3Scale(pair.source.worldBind.position, unitScale);
  const tgtBind = pair.target.worldBind.position;
  const srcHeight = upComponent(scaledSrcBind, srcUpAxis);
  // Hip-HEIGHT ratio, each side along its own up axis — Gen-2's proportional
  // delta scaling, clamped against degenerate binds. Full vector length would
  // fold a laterally offset hip into root-motion scale (Codex review, PR #4).
  const raw = srcHeight > 0 ? upComponent(tgtBind, tgtUpAxis) / srcHeight : 1;
  const deltaRatio = Math.min(DELTA_RATIO_MAX, Math.max(DELTA_RATIO_MIN, raw));

  const values = new Float32Array(track.values.length);
  for (let k = 0; k < track.times.length; k += 1) {
    const i = k * 3;
    // Track values are localBind-space (the §2b contract) — lift to world
    // through the source hips-parent bind, THEN unit-normalize.
    const srcLocal: Vec3Data = {
      x: track.values[i] as number,
      y: track.values[i + 1] as number,
      z: track.values[i + 2] as number,
    };
    const srcPos = vec3Scale(parentToWorld(srcLocal, srcParent), unitScale);
    // FACING/UP NORMALIZATION (research digest §2.5): excursions are WORLD
    // vectors, so declared-convention differences rotate them RIGIDLY here —
    // never via binds/boneInverses (the double-rotation scar). Bind-relative
    // rotation transfer is body-relative and needs no correction.
    let excursion = vec3Scale(vec3Sub(srcPos, scaledSrcBind), deltaRatio);
    if (convention !== null) excursion = quatRotateVec3(convention, excursion);
    // World result, expressed where the playback contract expects it: the
    // TARGET hips-parent's localBind space (a Tripo Z-up Root otherwise
    // turns forward travel into climbing — deep dive §7, measured).
    const out = worldToParent(vec3Add(tgtBind, excursion), tgtParent);
    values[i] = out.x;
    values[i + 1] = out.y;
    values[i + 2] = out.z;
  }
  return {
    track: { target: pair.target.name, path: 'translation', times: track.times.slice(), values },
    deltaRatio,
  };
}

/**
 * Retarget a clip from the source rig to the target rig through the ontology.
 * Both rigs must cover `options.profile`; anything undeliverable is either a
 * typed throw (reflections, coverage) or a named entry in the report — never
 * a silent drop.
 */
export function retargetClip(
  ontology: Ontology,
  source: { rig: RigDescriptor; clip: ClipData },
  target: { rig: RigDescriptor },
  options: RetargetOptions,
): RetargetResult {
  requireProfileCoverage(source.rig, ontology, options.profile, 'retarget:source');
  requireProfileCoverage(target.rig, ontology, options.profile, 'retarget:target');
  guardReflections(source.rig, 'source');
  guardReflections(target.rig, 'target');

  const { pairs, witnessContradictions, authoredFrameRefusals, postureContradictions, anteriorCompletions } = buildPairs(ontology, source.rig, target.rig, options);
  const parentPairs = parentPairsByTargetChain(pairs, target.rig);
  const unitScale = source.rig.units.metersPerUnit / target.rig.units.metersPerUnit;
  const convention = conventionRotation(source.rig, target.rig);

  // Rest-anchored transport (ADR-019 amendment, the clavicle class): the
  // constant per-pair anchor R = (P·srcBind·F)⁻¹·tgtBind. Lazy + cached:
  // computing R needs the pair's own prefix, which recurses through any
  // anchored ancestors (none exist in BBSA today, but the composition is
  // sound by construction); it is consulted both by the anchored bone's own
  // track and by every child's prefix — and it must go through the SAME
  // properRotationOf door, so an undetermined frame throws identically
  // whether the bone or its child is the one being played (one door, §7).
  const anchors = new Map<string, Quat>();
  const prefixFor = (key: string, pair: BonePair): Quat => {
    const parentPair = parentPairs.get(key) ?? null;
    // A parent's anchor is cancelled in the child's prefix (the arm must not
    // ride the shoulder correction) UNLESS the parent declares its children
    // ride it — a digit is one thing (ADR-019 §2.2): the child's prefix then
    // carries no R_p⁻¹ and wears the parent's kept rest.
    const parentAnchor =
      parentPair !== null && (parentPair.restAnchored || parentPair.twistAnchored) && !parentPair.childrenRide
        ? anchorOf(parentPair.source.name, parentPair)
        : null;
    return transportPrefix(pair, parentPair, source.rig, target.rig, convention, parentAnchor);
  };
  const anchorOf = (key: string, pair: BonePair): Quat => {
    const cached = anchors.get(key);
    if (cached !== undefined) return cached;
    const prefix = prefixFor(key, pair);
    const srcBind = quatNormalize(pair.source.localBind.rotation);
    const tgtBind = quatNormalize(pair.target.localBind.rotation);
    // The output the plain absolute law would emit at source-rest…
    const restOut = quatMultiply(prefix, quatMultiply(srcBind, properRotationOf(pair)));
    // …and the constant that carries it onto the target's own rest instead.
    const full = quatNormalize(quatMultiply(quatInvertUnit(restOut), tgtBind));
    let anchor = full;
    if (pair.twistAnchored) {
      // Keep only the roll about the target bone's own axis: the residual at
      // source-rest is then a pure swing (the source's pose) wearing the
      // target's own bind twist — its nail (ADR-019 §2.2).
      if (pair.targetAxisLocal === null) {
        throw new TwistAnchorAxisError(pair.target.name);
      }
      anchor = quatNormalize(swingTwist(full, pair.targetAxisLocal).twist);
    }
    anchors.set(key, anchor);
    return anchor;
  };

  const outTracks: ClipTrack[] = [];
  const dropped: string[] = [];
  const reorientation: ReorientReportEntry[] = [];
  let rotationCount = 0;
  let rootMotion: RetargetReport['rootMotion'] = null;
  let rootTarget: string | null = null;

  for (const track of source.clip.tracks) {
    const pair = pairs.get(track.target);
    if (track.path === 'rotation' && pair) {
      const prefix = prefixFor(track.target, pair);
      const anchor = pair.restAnchored || pair.twistAnchored ? anchorOf(track.target, pair) : null;
      outTracks.push(retargetRotationTrack(track, pair, prefix, anchor));
      rotationCount += 1;
      // retargetRotationTrack threw on undetermined, so this narrows safely.
      if (pair.reorient.kind === 'frameMap') {
        reorientation.push({
          target: pair.target.name,
          confidence: pair.reorient.confidence,
          evidence: pair.reorient.evidence,
          degradedBecause: pair.reorient.degradedBecause,
          residualRadians: pair.reorient.residualRadians,
          restAnchored: pair.restAnchored,
          twistAnchored: pair.twistAnchored,
          childrenRide: pair.childrenRide,
        });
      }
    } else if (
      track.path === 'translation' &&
      pair &&
      track.target === source.clip.rootMotionTarget
    ) {
      // Resolve the hips-parent frames on both sides (§2b track-space contract).
      const parentFrameOf = (bone: RigBone, rig: RigDescriptor): ParentFrame | null => {
        if (bone.parent === null) return null;
        const parent = rig.bones.find((b) => b.name === bone.parent);
        if (!parent) return null; // parseRigDescriptor guarantees this; belt over suspenders
        return { name: parent.name, ...parent.worldBind };
      };
      // The lift below uses STATIC ancestor binds — an ancestor with its own
      // clip tracks would be silently ignored (its rotation redirects every
      // excursion; its translation vanishes). Refuse loudly (Sol, PR #18).
      const srcByName = new Map(source.rig.bones.map((b) => [b.name, b]));
      for (
        let ancestor = pair.source.parent;
        ancestor !== null;
        ancestor = srcByName.get(ancestor)?.parent ?? null
      ) {
        const ancestorName = ancestor;
        const animated = source.clip.tracks
          .filter((t) => t.target === ancestorName && t.path !== 'morph')
          .map((t) => `${t.target}/${t.path}`);
        if (animated.length > 0) {
          throw new AnimatedParentTrajectoryError(pair.source.name, ancestorName, animated);
        }
      }
      const srcParent = parentFrameOf(pair.source, source.rig);
      const tgtParent = parentFrameOf(pair.target, target.rig);
      const { track: outTrack, deltaRatio } = retargetRootPosition(
        track,
        pair,
        srcParent,
        tgtParent,
        unitScale,
        source.rig.upAxis,
        target.rig.upAxis,
        convention,
      );
      outTracks.push(outTrack);
      rootTarget = pair.target.name;
      rootMotion = {
        applied: true,
        unitScale,
        deltaRatio,
        conventionApplied: convention !== null,
        sourceParentBone: srcParent?.name ?? null,
        targetParentBone: tgtParent?.name ?? null,
      };
    } else {
      dropped.push(`${track.target}/${track.path}`);
    }
  }

  if (outTracks.length === 0) {
    // Nothing landed: every source track names a bone the target cannot carry
    // (a face-only SL expression against a body, ADR-191 R4). Say how many and
    // which — ALL of them: a bulk report keeps only this message, and a
    // truncated list cannot say which joints failed (Codex P2, #145).
    const err = new UnmappedBoneError(source.clip.tracks[0]?.target ?? '(none)', 'retarget');
    err.message += ` No track landed — all ${dropped.length} dropped: ${dropped.join(', ')}.`;
    throw err;
  }

  // Dogfood the boundary: the output must satisfy the same contract inputs do.
  const clip = parseClipData({
    name: source.clip.name,
    fps: source.clip.fps,
    durationSec: source.clip.durationSec,
    additive: source.clip.additive,
    rootMotionTarget: rootTarget,
    tracks: outTracks,
  });

  return {
    clip,
    report: {
      retargetedRotationTracks: rotationCount,
      droppedTracks: dropped,
      reorientation,
      twistWitnessContradictions: witnessContradictions,
      authoredFrameRefusals,
      postureWitnessContradictions: postureContradictions,
      anteriorFrameCompletions: anteriorCompletions,
      rootMotion,
    },
  };
}
